import React, { useEffect, useState } from "react";
import "./community.css";
import {
  Box, Button, Card, CardContent, Avatar, Typography, IconButton,
  TextField, InputAdornment, Tabs, Tab, Stack, Dialog, DialogTitle,
  DialogContent, DialogActions, CircularProgress, Menu, MenuItem
} from "@mui/material";
import {
  ArrowBack, Menu as MenuIcon, Search, AddCircleOutline,
  PersonAddAlt, Image as ImageIcon, Person, Close
} from "@mui/icons-material";
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import backend from "../../../../../api";
import { useSnackbar } from "notistack";

/** upload helper (web) */
const UPLOAD_URL = "https://aws-calendia-bc10efcc84bf.herokuapp.com/api/deck/upload";
async function uploadWeb(files) {
  const fd = new FormData();
  [...files].forEach(f => fd.append("files", f));
  const r = await backend.post(UPLOAD_URL, fd, { headers: { "Content-Type": "multipart/form-data" } });
  return (r.data?.data || []).map(d => d.imgurl);
}

/** Small card used on Explore */
function CommunityCard({ item, onClick, onJoin }) {
  const joined = !!item._joined;
  return (
    <Card elevation={1} sx={{ borderRadius: 2, border: "1px solid #eee" }}>
      <CardContent sx={{ p: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
          <Avatar src={item.avatar || item.coverImage} sx={{ width: 44, height: 44 }} />
          <Box flex={1} minWidth={0}>
            <Typography noWrap className="comm-cname">{item.name}</Typography>
            <Typography noWrap className="comm-cmeta">
              {(item.members?.length ?? 0)} members • {item.privacy}
            </Typography>
          </Box>
        </Stack>

        <Typography className="comm-cdesc" sx={{ mt: .5 }} gutterBottom>
          {item.description || "Lorem Ipsum is simply dummy text of the printing…"}
        </Typography>

        <Stack direction="row" spacing={1}>
          <Button
            onClick={joined ? onClick : onJoin}
            className={`comm-cta ${joined ? "joined" : ""}`}
            fullWidth
          >
            {joined ? "View" : item.privacy === "private" ? "Invite only" : "Join"}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

/** Create community dialog */
function CreateCommunityDialog({ open, onClose, onCreated }) {
  const { user } = useSelector(s => s.auth);
  const { enqueueSnackbar } = useSnackbar();
  const userId = user?._id;

  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [privacy, setPrivacy] = useState("public");
  const [cover, setCover] = useState("");
  const [avatar, setAvatar] = useState("");
  const [busy, setBusy] = useState(false);

  const handlePick = async (e, setter) => {
    try {
      const urls = await uploadWeb(e.target.files);
      if (urls[0]) setter(urls[0]);
    } catch (err) {
      enqueueSnackbar("Failed to upload image", { variant: "error" });
    }
  };

  const removeImage = (setter) => {
    setter("");
  };

  const submit = async () => {
    if (!userId) {
      enqueueSnackbar("Please log in to create a community", { variant: "error" });
      return;
    }
    if (!name.trim()) {
      enqueueSnackbar("Please enter a community name", { variant: "error" });
      return;
    }
    if (!desc.trim()) {
      enqueueSnackbar("Please enter a community description", { variant: "error" });
      return;
    }
    try {
      setBusy(true);
      await backend.post("/api/community", {
        userId, name, description: desc, privacy, coverImage: cover, avatar
      });
      enqueueSnackbar("Community created successfully", { variant: "success" });
      setName(""); setDesc(""); setPrivacy("public"); setCover(""); setAvatar("");
      onCreated?.(); onClose();
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || "Failed to create community", { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        Create community
        <IconButton onClick={onClose} sx={{ float: "right" }}><Close /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          <TextField
            label="Community name"
            value={name}
            onChange={e => setName(e.target.value)}
            error={!name.trim()}
            helperText={!name.trim() ? "Community name is required" : ""}
          />
          <TextField
            label="Short description"
            value={desc}
            onChange={e => setDesc(e.target.value)}
            multiline
            rows={3}
            error={!desc.trim()}
            helperText={!desc.trim() ? "Description is required" : ""}
          />
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<ImageIcon />}
              className="comm-pick"
              component="label"
              sx={{ flex: 1 }}
              disabled={busy}
            >
              {cover ? "Change cover" : "Upload cover"}
              <input type="file" accept="image/*" hidden onChange={(e) => handlePick(e, setCover)} />
            </Button>
            {cover && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => removeImage(setCover)}
                sx={{ width: 160 }}
                disabled={busy}
              >
                Remove cover
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<Person />}
              className="comm-pick"
              component="label"
              sx={{ width: 160 }}
              disabled={busy}
            >
              {avatar ? "Change avatar" : "Upload avatar"}
              <input type="file" accept="image/*" hidden onChange={(e) => handlePick(e, setAvatar)} />
            </Button>
            {avatar && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => removeImage(setAvatar)}
                sx={{ width: 160 }}
                disabled={busy}
              >
                Remove avatar
              </Button>
            )}
          </Stack>
          {cover && (
            <Box sx={{ position: "relative", borderRadius: 2, overflow: "hidden" }}>
              <img
                src={cover}
                alt=""
                style={{ height: 120, width: "100%", objectFit: "cover" }}
                loading="lazy"
              />
            </Box>
          )}
          {avatar && <Avatar src={avatar} sx={{ width: 60, height: 60 }} />}
          <Tabs value={privacy} onChange={(_, v) => setPrivacy(v)} className="comm-tabs">
            <Tab value="public" label="public" className="comm-tab" />
            <Tab value="private" label="private" className="comm-tab" />
          </Tabs>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={submit} className="comm-cta" disabled={busy}>
          {busy ? <CircularProgress size={18} /> : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Invite dialog (email/phone) */
function InviteDialog({ open, onClose, onInvited }) {
  const { user } = useSelector(s => s.auth);
  const { enqueueSnackbar } = useSnackbar();
  const userId = user?._id;

  const [mine, setMine] = useState([]);
  const [communityId, setCommunityId] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !userId) return;
    backend.get("/api/community", { params: { tab: "joined", userId } })
      .then(r => setMine((r.data?.data || []).filter(c => c._isAdmin)))
      .catch(() => {
        enqueueSnackbar("Failed to load communities", { variant: "error" });
      });
  }, [open, userId]);

  const submit = async () => {
    if (!userId) {
      enqueueSnackbar("Please log in to invite members", { variant: "error" });
      return;
    }
    if (!communityId) {
      enqueueSnackbar("Please select a community", { variant: "error" });
      return;
    }
    if (!email.trim() && !phone.trim()) {
      enqueueSnackbar("Please enter an email or phone number", { variant: "error" });
      return;
    }
    try {
      setBusy(true);
      await backend.post(`/api/community/${communityId}/add-members`, {
        userId, emails: email ? [email.trim()] : [], phones: phone ? [phone.trim()] : []
      });
      enqueueSnackbar("Invitation sent successfully", { variant: "success" });
      setEmail(""); setPhone(""); setCommunityId("");
      onInvited?.(); onClose();
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || "Failed to send invitation", { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        Add people
        <IconButton onClick={onClose} sx={{ float: "right" }}><Close /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          <TextField
            label="Community ID"
            value={communityId}
            onChange={e => setCommunityId(e.target.value)}
            error={!communityId}
            helperText={!communityId ? "Community ID is required" : ""}
          />
          <TextField
            label="Email (registered)"
            value={email}
            onChange={e => setEmail(e.target.value)}
          />
          <TextField
            label="Phone (registered)"
            value={phone}
            onChange={e => setPhone(e.target.value)}
          />
          <Typography sx={{ color: "var(--subtle)" }}>Pick from your admin groups:</Typography>
          <Stack spacing={1} sx={{ maxHeight: 160, overflow: "auto" }}>
            {mine.map(c => (
              <Card key={c._id} variant="outlined" onClick={() => setCommunityId(c._id)} sx={{ p: 1.2, cursor: "pointer" }}>
                <Typography fontWeight={700}>{c.name}</Typography>
                <Typography variant="caption" sx={{ color: "var(--subtle)" }}>{c._id}</Typography>
              </Card>
            ))}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={submit} className="comm-cta" disabled={busy}>
          {busy ? <CircularProgress size={18} /> : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CommunityExplore() {
  const nav = useNavigate();
  const { user } = useSelector(s => s.auth);
  const { enqueueSnackbar } = useSnackbar();
  const userId = user?._id;

  const [tab, setTab] = useState("explore");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [menuEl, setMenuEl] = useState(null);
  const [openCreate, setOpenCreate] = useState(false);
  const [openInvite, setOpenInvite] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { tab, q: q.trim() };
      if (userId) params.userId = userId;
      const r = await backend.get("/api/community", { params });
      let data = r.data?.data || [];
      if (tab === "explore" && userId) data = data.filter(c => !c._joined);
      setRows(data);
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || "Failed to load communities", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [tab, userId]); // eslint-disable-line
  useEffect(() => { const t = setTimeout(fetchData, 300); return () => clearTimeout(t); }, [q]); // eslint-disable-line

  const onJoin = async (c) => {
    if (c.privacy === "private" || !userId) {
      enqueueSnackbar("Please log in to join a community", { variant: "error" });
      nav("/login");
      return;
    }
    try {
      await backend.put(`/api/community/${c._id}/join-toggle`, { userId });
      enqueueSnackbar("Successfully joined community", { variant: "success" });
      fetchData();
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || "Failed to join community", { variant: "error" });
    }
  };

  return (
    <Box className="comm-page">
      {/* Top Row */}
      <Stack direction="row" spacing={1.5} alignItems="center" className="comm-toprow">
        <IconButton onClick={() => nav(-1)}><ArrowBack /></IconButton>
        <Typography fontWeight={800} color="var(--dark)" sx={{ flex: 1, textAlign: "center" }}>Community</Typography>
        <IconButton onClick={(e) => setMenuEl(e.currentTarget)}><MenuIcon /></IconButton>
      </Stack>

      {/* Search */}
      <TextField
        placeholder="Search"
        size="medium"
        fullWidth
        sx={{ mt: 1.5 }}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        InputProps={{
          startAdornment: <InputAdornment position="start"><Search sx={{ color: "var(--subtle)" }} /></InputAdornment>,
          sx: { borderRadius: 2 }
        }}
      />

      {/* Tabs */}
      <Box sx={{ mt: 1.5 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} className="comm-tabs">
          <Tab value="explore" label="Explore" className="comm-tab" />
          <Tab value="joined" label="Joined" className="comm-tab" />
        </Tabs>
      </Box>

      <Typography sx={{ mt: 1.5 }} className="comm-cname">Total communities</Typography>
      <Typography sx={{ color: "var(--subtle)", mb: 1 }}>Public groups are open; private groups are by invite only.</Typography>

      {/* List */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}><CircularProgress sx={{ color: "var(--brand)" }} /></Box>
      ) : (
        <Stack spacing={1.2} sx={{ pb: 4 }}>
          {rows.map(item => (
            <CommunityCard
              key={item._id}
              item={item}
              onClick={() => nav(`/community-details/${item._id}`)}
              onJoin={() => onJoin(item)}
            />
          ))}
        </Stack>
      )}

      {/* Menu */}
      <Menu anchorEl={menuEl} open={Boolean(menuEl)} onClose={() => setMenuEl(null)}>
        <MenuItem onClick={() => { setMenuEl(null); setOpenCreate(true); }}>
          <AddCircleOutline fontSize="small" sx={{ mr: 1 }} /> Create community
        </MenuItem>
        <MenuItem onClick={() => { setMenuEl(null); setOpenInvite(true); }}>
          <PersonAddAlt fontSize="small" sx={{ mr: 1 }} /> Add people (email/phone)
        </MenuItem>
      </Menu>

      <CreateCommunityDialog open={openCreate} onClose={() => setOpenCreate(false)} onCreated={fetchData} />
      <InviteDialog open={openInvite} onClose={() => setOpenInvite(false)} onInvited={fetchData} />
    </Box>
  );
}

export default CommunityExplore;